import {Observable} from 'rxjs';
import {
  clareSpecToSpecification,
  clareVariationToVariant,
  Specification,
  Variant,
} from '../../model';
import {Injectable} from '@angular/core';
import {CommerceApiService} from './commerce-api.service';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {map} from 'rxjs/operators';
import {ApiModule} from '../api.module';
import {
  ClareApiResponse,
  ProductSpec,
  SpecOption,
  Variation,
} from '@sportngin/se-commerce-sdk/models/clare';

type ProductSpecAccumulatorEntry = {
  spec: ProductSpec;
  options: Map<number, SpecOption>;
};

/**
 * Returns the accumulator row for a product_spec, creating it when missing.
 */
function getOrCreateSpecAccumulator(
  bySpecId: Map<number, ProductSpecAccumulatorEntry>,
  productSpec: NonNullable<SpecOption['product_spec']>,
): ProductSpecAccumulatorEntry {
  let entry = bySpecId.get(productSpec.id!);
  if (!entry) {
    entry = {
      spec: {
        id: productSpec.id!,
        name: productSpec.name,
        display_order: productSpec.display_order,
        spec_options: [],
      },
      options: new Map(),
    };
    bySpecId.set(productSpec.id!, entry);
  }
  return entry;
}

/**
 * Adds a spec option to the accumulator when not already present for that option id.
 */
function addUniqueSpecOption(
  entry: ProductSpecAccumulatorEntry,
  opt: SpecOption,
): void {
  if (!entry.options.has(opt.id!)) {
    entry.options.set(opt.id!, opt);
  }
}

/**
 * Adds a spec option to the product_spec accumulator when the option is linked to a spec.
 */
function addProductSpecOption(
  bySpecId: Map<number, ProductSpecAccumulatorEntry>,
  opt: SpecOption,
): void {
  const productSpec = opt.product_spec;
  if (!productSpec) return;

  const entry = getOrCreateSpecAccumulator(bySpecId, productSpec);
  addUniqueSpecOption(entry, opt);
}

/**
 * Flattens spec accumulators into ProductSpec[] with deduped spec_options lists.
 */
function specAccumulatorsToProductSpecs(
  entries: Iterable<ProductSpecAccumulatorEntry>,
): ProductSpec[] {
  return Array.from(entries).map(e => ({
    ...e.spec,
    spec_options: Array.from(e.options.values()),
  }));
}

/**
 * Derives ProductSpec[] from catalog_product_variations response by grouping
 * each variation's spec_options by product_spec, so specs and variants can come from one endpoint.
 */
function productSpecsFromVariations(variations: Variation[]): ProductSpec[] {
  const bySpecId = new Map<number, ProductSpecAccumulatorEntry>();
  variations
    .flatMap(variation => variation.spec_options ?? [])
    .forEach(opt => addProductSpecOption(bySpecId, opt));

  return specAccumulatorsToProductSpecs(bySpecId.values());
}

export interface SpecVariantApi {
  variants(productId: string): Observable<Variant[]>;
  getSpecs(query: string): Observable<Specification[]>;
}

@Injectable({providedIn: ApiModule})
export class SpecVariantApiService
  extends CommerceApiService
  implements SpecVariantApi
{
  controllerPath = 'finance/items';

  constructor(
    protected http: HttpClient,
    protected store: Store,
  ) {
    super(http, store);
  }

  /**
   * Gets specs from the same endpoint as variants (catalog_product_variations),
   * so one endpoint supplies both specs and variants.
   */
  getSpecs(productId: string): Observable<Specification[]> {
    const url = this.getProductsUrl(`${productId}/catalog_product_variations`);
    return this.GET<ClareApiResponse<Variation[]>>(
      url,
      this.getOptions({withCredentials: true}),
    ).pipe(
      map(resp => {
        const productSpecs = productSpecsFromVariations(resp.result);
        return productSpecs.map(clareSpecToSpecification);
      }),
    );
  }

  variants(productId: string): Observable<Variant[]> {
    const path = this.getProductsUrl(`${productId}/catalog_product_variations`);
    return this.GET<ClareApiResponse<Variation[]>>(
      path,
      this.getOptions({withCredentials: true}),
    ).pipe(map(resp => resp.result.map(clareVariationToVariant)));
  }

  private getProductsUrl(path: string): string {
    return super.getUrl(`finance/products/${path}`);
  }
}
